import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api.js'
import {
  Badge, Card, CopyButton, CopyRow, Empty, ErrorBox, Field, Modal, Page, Select,
  Spinner, SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { fmtStamp } from '../../lib/format.js'
import { whatsappUrl } from '../../lib/share.js'

const ROLE_TONE = { admin: 'brand', operator: 'info', hatchery: 'good', liaison: 'warn' }

const ROLES = [
  { value: 'operator', label: 'Operator — runs the desk, matches and books' },
  { value: 'liaison', label: 'Liaison officer — rides with the seed, field screen' },
  { value: 'admin', label: 'Admin — onboards hatcheries, issues logins' },
  { value: 'hatchery', label: 'Hatchery — posts batches for one hatchery' }
]

const BLANK = { role: 'operator', displayName: '', phone: '', baseLocation: '', hatcheryId: '', username: '', password: '' }

export default function Users () {
  const { data, error, loading, reload } = useAsync(() => api.get('/admin/users'), [], { live: ['users'] })
  const toast = useToast()
  const confirm = useConfirm()
  const [reveal, setReveal] = useState(false)
  const [adding, setAdding] = useState(false)
  const [filter, setFilter] = useState('all')

  const reset = async u => {
    const ok = await confirm({
      title: `Reset the password for ${u.username}?`,
      body: 'The current password stops working immediately. You will need to read the new one out to them.',
      facts: [['Account', u.displayName], ['Role', u.role]],
      confirmLabel: 'Reset password',
      tone: 'danger'
    })
    if (!ok) return
    try {
      const res = await api.post(`/admin/users/${u._id}/reset`, {})
      toast.ok(`${res.credentials.username} → ${res.credentials.password}`)
      setReveal(true)
      reload()
    } catch (err) { toast.error(err.message) }
  }

  const toggle = async u => {
    const disabling = u.active
    const ok = await confirm({
      title: disabling ? `Disable ${u.username}?` : `Enable ${u.username}?`,
      body: disabling
        ? 'They are signed out on their next request and cannot sign in again until you enable this login.'
        : 'They can sign in again with the password already on file.',
      facts: [['Account', u.displayName], ['Role', u.role]],
      confirmLabel: disabling ? 'Disable login' : 'Enable login',
      tone: disabling ? 'danger' : ''
    })
    if (!ok) return
    try {
      await api.post(`/admin/users/${u._id}/active`, { active: u.active ? 0 : 1 })
      toast.ok(`Login ${u.active ? 'disabled' : 'enabled'} for ${u.username}`)
      reload()
    } catch (err) { toast.error(err.message) }
  }

  if (loading) return <Page title="Logins & staff"><Spinner /></Page>
  if (error) return <Page title="Logins & staff"><ErrorBox error={error} onRetry={reload} /></Page>

  const users = data.users.filter(u => filter === 'all' || u.role === filter)
  const countOf = role => data.users.filter(u => u.role === role).length

  return (
    <Page
      title="Logins & staff"
      subtitle="Every account on the platform. Passwords are issued here and handed over offline — there is no self-service reset, so this list is the record of who can sign in."
      actions={
        <>
          <button className="btn" onClick={() => setReveal(r => !r)}>
            {reveal ? 'Hide passwords' : 'Show passwords'}
          </button>
          <button className="btn primary" onClick={() => setAdding(true)}>Add a login</button>
        </>
      }
    >
      <div className="tabs">
        <button className={`tab ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>
          All <span className="tab-n">{data.users.length}</span>
        </button>
        {['admin', 'operator', 'liaison', 'hatchery'].map(r => (
          <button key={r} className={`tab ${filter === r ? 'on' : ''}`} onClick={() => setFilter(r)}>
            {r === 'liaison' ? 'Liaison' : r[0].toUpperCase() + r.slice(1)} <span className="tab-n">{countOf(r)}</span>
          </button>
        ))}
      </div>

      <Card padded={false}>
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th>Username</th><th>Role</th><th>Belongs to</th>
                <th>Password on file</th><th>Last sign-in</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u._id}>
                  <td className="mono">{u.username}<span className="t-sub">{u.displayName}</span></td>
                  <td><Badge tone={ROLE_TONE[u.role] || 'muted'}>{u.role}</Badge></td>
                  <td>
                    {u.hatchery
                      ? <Link className="rowlink" to={`/admin/hatcheries/${u.hatchery._id}`}>{u.hatchery.name}</Link>
                      : u.liaison
                        ? <>{u.liaison.name}<span className="t-sub">liaison officer</span></>
                        : <span className="muted">Platform staff</span>}
                  </td>
                  <td>
                    {u.issuedSecret
                      ? (reveal
                          ? <span className="mono">{u.issuedSecret}</span>
                          : <span className="muted mono">••••••••</span>)
                      : <span className="muted small">set by the holder</span>}
                    {u.issuedAt && <span className="t-sub">issued {fmtStamp(u.issuedAt)}</span>}
                  </td>
                  <td className="small">{fmtStamp(u.lastLoginAt)}</td>
                  <td><Badge tone={u.active ? 'good' : 'muted'}>{u.active ? 'Enabled' : 'Disabled'}</Badge></td>
                  <td className="right nowrap">
                    <button className="btn sm" onClick={() => reset(u)}>Reset</button>{' '}
                    {u.role !== 'admin' && (
                      <button className="btn sm" onClick={() => toggle(u)}>{u.active ? 'Disable' : 'Enable'}</button>
                    )}
                  </td>
                </tr>
              ))}
              {!users.length && <tr><td colSpan={7}><Empty>No accounts in this group yet.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {adding && <AddLogin onClose={() => setAdding(false)} onDone={reload} />}
    </Page>
  )
}

/** Onboard a person: role first, because the role decides the rest of the form. */
function AddLogin ({ onClose, onDone }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [form, setForm] = useState(BLANK)
  const [busy, setBusy] = useState(false)
  const [issued, setIssued] = useState(null)

  const hatcheries = useAsync(() => api.get('/admin/hatcheries'))
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: 'Create this login?',
      body: 'The account can sign in as soon as it exists. The password is shown once here and stays readable until they change it.',
      facts: [['Name', form.displayName], ['Role', form.role], ['Username', form.username || 'derived from the name']],
      confirmLabel: 'Create login'
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await api.post('/admin/users', form)
      setIssued({ ...res, role: form.role, displayName: form.displayName, phone: form.phone })
      toast.ok(res.message)
      onDone?.()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (issued) {
    const handover = `Aqua Platform login for ${issued.displayName}
Sign in: ${window.location.origin}/login
Username: ${issued.credentials.username}
Password: ${issued.credentials.password}`
    return (
      <Modal title="Login issued" onClose={onClose}>
        <p className="muted small" style={{ marginTop: 0 }}>
          Read it out now. It stays readable on this screen until {issued.displayName} changes the password.
        </p>
        <CopyRow label="Username" value={issued.credentials.username} />
        <CopyRow label="Password" value={issued.credentials.password} />
        <div className="btnrow">
          <CopyButton className="btn primary" value={handover} told="Handover message copied">Copy the whole message</CopyButton>
          <a className="btn" href={whatsappUrl(handover, issued.phone)} target="_blank" rel="noreferrer">Send on WhatsApp</a>
          <button className="btn" onClick={onClose}>Done</button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Add a login" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Role" required hint="This decides which screens they can open.">
          <Select value={form.role} onChange={v => set('role', v)} options={ROLES} />
        </Field>

        <div className="formgrid two">
          <Field label="Full name" required>
            <input value={form.displayName} onChange={e => set('displayName', e.target.value)} required autoFocus />
          </Field>
          <Field label="Phone" required={form.role === 'liaison'}
            hint={form.role === 'liaison' ? 'The field screen and pond-side handover run on this number.' : undefined}>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 …" />
          </Field>
        </div>

        {form.role === 'liaison' && (
          <Field label="Base location" hint="Where the officer normally starts the day.">
            <input value={form.baseLocation} onChange={e => set('baseLocation', e.target.value)} placeholder="Kakinada" />
          </Field>
        )}

        {form.role === 'hatchery' && (
          <Field label="Hatchery" required hint="Only hatcheries without a login can be picked — reset the password instead if one exists.">
            <Select
              value={form.hatcheryId}
              onChange={v => set('hatcheryId', v)}
              placeholder={hatcheries.loading ? 'Loading…' : 'Choose a hatchery'}
              options={(hatcheries.data?.hatcheries || [])
                .filter(h => !h.login)
                .map(h => ({ value: h._id, label: `${h.name} · ${h.code}` }))}
            />
          </Field>
        )}

        <div className="formgrid two">
          <Field label="Username" hint="Blank = derived from the name.">
            <input value={form.username} onChange={e => set('username', e.target.value)} autoComplete="off" />
          </Field>
          <Field label="Password" hint="Blank = a readable one is generated.">
            <input value={form.password} onChange={e => set('password', e.target.value)} autoComplete="off" />
          </Field>
        </div>

        <div className="btnrow">
          <SubmitButton busy={busy} type="submit">Create login</SubmitButton>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  )
}
